import { useQuery } from '@tanstack/react-query';

import { DAAS_TOKEN_METADATA_CACHE, getQueryString, useAppSelector } from '@pkg/hooks';
import { useGetLiquidIDItems } from '@pkg/hooks/liquid-id/useGetLiquidIdItems';

import { type SupportedTokens, type TokenV2Metadata } from '@app/client';
import { callGetTokenMetadata } from '@app/client/resources/daas';

export const useGetPortfolioLid = () => {
    const ownerId = useAppSelector((state) => state.yatPage.user_id);
    return useGetLiquidIDItems(ownerId || '');
};

export const useGetPortfolioLidTokensData = (ownerId?: string) => {
    const yatPageOwnerId = useAppSelector((state) => state.yatPage.user_id);
    const { data: lidResponseData, isLoading } = useGetLiquidIDItems(ownerId || yatPageOwnerId || '');

    const customQueryString = getQueryString({
        queryArrayKey: 'token_ids',
        queryArray: lidResponseData?.items,
    });

    const cache = [DAAS_TOKEN_METADATA_CACHE, ...(lidResponseData?.items || [])];

    return useQuery(
        cache,
        ({ signal }) =>
            callGetTokenMetadata({
                settings: { signal },
                customQuery: customQueryString,
            }),
        {
            keepPreviousData: false,
            retry: false,
            enabled: !isLoading,
            select: (res) => {
                if (res.ok && res.value) {
                    return lidResponseData?.items.reduce<TokenV2Metadata[]>((acc, lidItem) => {
                        const [chain, contract, tokenId] = lidItem.split('.');
                        const token = res.value[chain.toLocaleLowerCase() as SupportedTokens]?.find(
                            (item) => item.collection === contract && item.token_id === tokenId,
                        );
                        if (token) {
                            return [
                                ...acc,
                                {
                                    ...token,
                                    queryFormat: lidItem,
                                    image_url: token.image_url?.replace(',q_fallback', ''),
                                },
                            ];
                        }
                        return acc;
                    }, []);
                }
            },
        },
    );
};
